import React, { useEffect, useMemo, useState } from "react";
import { X, AlertCircle, Wand2, Lock } from "lucide-react";
import {
  ProjectSetup,
  ProjectDocument,
  Shot,
  ShotCodeGeneratorEpisodeState,
  ShotCodeGeneratorSequenceState,
} from "../types";
import { v4 as uuidv4 } from "uuid";
import { cn } from "../utils";
import { parseShotCodeBreadcrumb } from "../shared/shotCodeParser";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  setup: ProjectSetup;
  shots: Shot[];
  onGenerate: (newShots: Shot[], updateExisting?: boolean, newShowCode?: string) => void;
  onUpdateSetup: (updates: Partial<ProjectSetup>) => void;
  documents?: ProjectDocument[];
  isEmbedded?: boolean;
}

type ScriptEpisodeSummary = {
  episodeNumber: number;
  sceneCount: number;
  sourceDocumentId?: string;
  sourceDocumentName?: string;
};

const DEFAULT_SHOT_COUNT = 5;

function extractEpisodeNumber(document: ProjectDocument): number | null {
  if (!document.episode) return null;
  const match = document.episode.match(/(\d+)$/);
  if (!match) {
    const numeric = Number(document.episode);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  }
  const numStr = match[1];
  const value =
    numStr.length >= 3
      ? parseInt(numStr.slice(-2), 10)
      : parseInt(numStr, 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function buildScriptEpisodeSummaries(documents: ProjectDocument[]): Record<number, ScriptEpisodeSummary> {
  return documents.reduce<Record<number, ScriptEpisodeSummary>>((acc, document) => {
    const episodeNumber = extractEpisodeNumber(document);
    if (!episodeNumber) return acc;
    const sceneCount = Math.max(0, Number(document.sceneCount || 0));
    const existing = acc[episodeNumber];
    if (!existing || sceneCount > existing.sceneCount) {
      acc[episodeNumber] = {
        episodeNumber,
        sceneCount,
        sourceDocumentId: document.id,
        sourceDocumentName: document.name,
      };
    }
    return acc;
  }, {});
}

function buildDefaultSequences(
  count: number,
  existing: Record<number, ShotCodeGeneratorSequenceState> | undefined,
  defaultShotCount: number,
  autoSelected: boolean,
): Record<number, ShotCodeGeneratorSequenceState> {
  const safeCount = Math.max(0, count);
  const sequences: Record<number, ShotCodeGeneratorSequenceState> = {};
  for (let index = 1; index <= safeCount; index += 1) {
    const previous = existing?.[index];
    sequences[index] = {
      ...previous,
      sceneNumber: previous?.sceneNumber ?? index,
      manualShotCount: previous?.manualShotCount ?? defaultShotCount,
      selected: previous?.selected ?? autoSelected,
    };
  }
  return sequences;
}

function buildGeneratorEpisodes(
  setup: ProjectSetup,
  scriptEpisodes: Record<number, ScriptEpisodeSummary>,
  expectedEpisodeCount: number,
  manualOverrideEnabled: boolean,
): Record<number, ShotCodeGeneratorEpisodeState> {
  const savedEpisodes = setup.shotCodeGeneratorEpisodes || {};
  const legacySequenceData = setup.episodeSequenceData || {};
  const detectedEpisodeNumbers = Object.keys(scriptEpisodes).map(Number);
  const maxDetectedEpisode = detectedEpisodeNumbers.length > 0 ? Math.max(...detectedEpisodeNumbers) : 0;
  const maxSavedEpisode = Object.keys(savedEpisodes).length > 0 ? Math.max(...Object.keys(savedEpisodes).map(Number)) : 0;
  const maxLegacyEpisode = Object.keys(legacySequenceData).length > 0 ? Math.max(...Object.keys(legacySequenceData).map(Number)) : 0;
  const totalEpisodeCount = Math.max(expectedEpisodeCount, maxDetectedEpisode, maxSavedEpisode, maxLegacyEpisode);
  const episodes: Record<number, ShotCodeGeneratorEpisodeState> = {};

  for (let episodeNumber = 1; episodeNumber <= totalEpisodeCount; episodeNumber += 1) {
    const scriptSummary = scriptEpisodes[episodeNumber];
    const savedEpisode = savedEpisodes[episodeNumber] || savedEpisodes[String(episodeNumber) as unknown as number];
    const legacySequenceCounts = legacySequenceData[String(episodeNumber)] || {};
    const legacyManualSequenceCount =
      Object.keys(legacySequenceCounts).length > 0
        ? Math.max(...Object.keys(legacySequenceCounts).map((value) => Number(value)))
        : undefined;
    const manualSequenceCount = savedEpisode?.manualSequenceCount ?? legacyManualSequenceCount;
    const detectedSequenceCount = scriptSummary?.sceneCount || savedEpisode?.detectedSequenceCount || undefined;
    const effectiveSequenceCount = manualOverrideEnabled
      ? manualSequenceCount ?? detectedSequenceCount ?? 0
      : detectedSequenceCount ?? manualSequenceCount ?? 0;

    const sequenceSeed: Record<number, ShotCodeGeneratorSequenceState> = {};
    Object.entries(legacySequenceCounts).forEach(([sequenceKey, shotCount]) => {
      const sequenceNumber = Number(sequenceKey);
      if (!Number.isFinite(sequenceNumber) || sequenceNumber <= 0) return;
      sequenceSeed[sequenceNumber] = {
        sceneNumber: sequenceNumber,
        manualShotCount: shotCount,
        selected: true,
      };
    });

    const savedSequences = savedEpisode?.sequences || {};
    Object.entries(savedSequences).forEach(([sequenceKey, sequence]) => {
      const sequenceNumber = Number(sequenceKey);
      if (!Number.isFinite(sequenceNumber) || sequenceNumber <= 0) return;
      sequenceSeed[sequenceNumber] = {
        ...sequenceSeed[sequenceNumber],
        ...sequence,
      };
    });

    const hasScriptData = Boolean(scriptSummary);
    const isManualEpisodeOverrideEnabled =
      savedEpisode?.isManualOverrideEnabled ??
      manualOverrideEnabled;

    episodes[episodeNumber] = {
      hasScriptData,
      isActive: hasScriptData || manualOverrideEnabled || Boolean(savedEpisode?.isActive),
      isManualOverrideEnabled: isManualEpisodeOverrideEnabled,
      sourceDocumentId: scriptSummary?.sourceDocumentId || savedEpisode?.sourceDocumentId,
      sourceDocumentName: scriptSummary?.sourceDocumentName || savedEpisode?.sourceDocumentName,
      detectedSequenceCount,
      manualSequenceCount,
      sequences: buildDefaultSequences(
        effectiveSequenceCount,
        sequenceSeed,
        setup.shotCount || DEFAULT_SHOT_COUNT,
        hasScriptData,
      ),
    };
  }

  return episodes;
}

function buildLegacyEpisodeSequenceData(
  episodes: Record<number, ShotCodeGeneratorEpisodeState>,
  defaultShotCount: number,
): Record<string, Record<string, number>> {
  return Object.entries(episodes).reduce<Record<string, Record<string, number>>>((acc, [episodeKey, episode]) => {
    const selectedSequences = Object.entries(episode.sequences).reduce<Record<string, number>>((sequenceAcc, [sequenceKey, sequence]) => {
      if (!sequence.selected) return sequenceAcc;
      sequenceAcc[sequenceKey] = Math.max(0, sequence.manualShotCount ?? sequence.detectedShotEstimate ?? defaultShotCount);
      return sequenceAcc;
    }, {});
    if (Object.keys(selectedSequences).length > 0) {
      acc[episodeKey] = selectedSequences;
    }
    return acc;
  }, {});
}

export function ShotCodeGenerator({
  isOpen,
  onClose,
  setup,
  shots,
  onGenerate,
  onUpdateSetup,
  documents = [],
  isEmbedded = false,
}: Props) {
  const breadcrumbConfig = useMemo(() => parseShotCodeBreadcrumb(setup.shotCodeBreadcrumb), [setup.shotCodeBreadcrumb]);
  const scriptEpisodes = useMemo(() => buildScriptEpisodeSummaries(documents), [documents]);
  const detectedEpisodeCount = useMemo(() => Object.keys(scriptEpisodes).length, [scriptEpisodes]);

  const [showCode, setShowCode] = useState<string>(setup.showCode || "");
  const [season, setSeason] = useState<number>(1);
  const [expectedEpisodeCount, setExpectedEpisodeCount] = useState<number>(
    setup.expectedEpisodeCount ?? setup.episodeCount ?? detectedEpisodeCount
  );
  const [manualEpisodeOverrideEnabled, setManualEpisodeOverrideEnabled] = useState<boolean>(
    setup.manualEpisodeOverrideEnabled ?? false
  );
  const [episodes, setEpisodes] = useState<Record<number, ShotCodeGeneratorEpisodeState>>(
    buildGeneratorEpisodes(
      setup,
      scriptEpisodes,
      setup.expectedEpisodeCount ?? setup.episodeCount ?? detectedEpisodeCount,
      setup.manualEpisodeOverrideEnabled ?? false,
    )
  );
  const [selectedEpisode, setSelectedEpisode] = useState<number | null>(null);
  const includeEpisodeInShotCodes = setup.projectType !== "feature_film" && setup.projectType !== "other";
  const [epPattern, setEpPattern] = useState(breadcrumbConfig ? "#".repeat(breadcrumbConfig.seasonEpisodeLength) : "000");
  const [seqPattern, setSeqPattern] = useState(breadcrumbConfig ? "#".repeat(breadcrumbConfig.sequenceLength) : "000");
  const [shotPattern, setShotPattern] = useState(breadcrumbConfig ? "#".repeat(breadcrumbConfig.shotLength) : "000");
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  useEffect(() => {
    const nextExpectedEpisodeCount = setup.expectedEpisodeCount ?? setup.episodeCount ?? detectedEpisodeCount;
    const nextManualOverride = setup.manualEpisodeOverrideEnabled ?? false;
    const nextEpisodes = buildGeneratorEpisodes(
      setup,
      scriptEpisodes,
      nextExpectedEpisodeCount,
      nextManualOverride,
    );

    setShowCode(setup.showCode || "");
    setExpectedEpisodeCount(nextExpectedEpisodeCount);
    setManualEpisodeOverrideEnabled(nextManualOverride);
    setEpisodes(nextEpisodes);
    if (breadcrumbConfig) {
      setEpPattern("#".repeat(breadcrumbConfig.seasonEpisodeLength));
      setSeqPattern("#".repeat(breadcrumbConfig.sequenceLength));
      setShotPattern("#".repeat(breadcrumbConfig.shotLength));
    }
    setSelectedEpisode((current) => {
      if (current && nextEpisodes[current]) return current;
      const firstActive = Object.entries(nextEpisodes).find(([, episode]) => episode.isActive);
      if (firstActive) return Number(firstActive[0]);
      const firstEpisode = Object.keys(nextEpisodes)[0];
      return firstEpisode ? Number(firstEpisode) : null;
    });
  }, [
    isEmbedded,
    setup,
    documents,
    detectedEpisodeCount,
    breadcrumbConfig,
    scriptEpisodes,
  ]);

  const generateShotCode = (epNum: number, seqNum: number, shotIndex: number) => {
    const epPartLen = 2;
    const seasonPartLen = Math.max(0, epPattern.length - epPartLen);
    const epStr = season.toString().padStart(seasonPartLen, "0") + epNum.toString().padStart(epPartLen, "0");
    const seqStr = seqNum.toString().padStart(seqPattern.length, "0");
    const shotStr = (shotIndex * 10).toString().padStart(shotPattern.length, "0");

    const codeParts = includeEpisodeInShotCodes
      ? [showCode, epStr, seqStr, shotStr]
      : [showCode, seqStr, shotStr];

    return codeParts.filter(Boolean).join("_");
  };

  const exampleShotCode = useMemo(
    () => generateShotCode(1, 1, 1),
    [showCode, season, epPattern, seqPattern, shotPattern, includeEpisodeInShotCodes]
  );
  const breadcrumbExample = useMemo(
    () => includeEpisodeInShotCodes ? "@@@_###_###_###" : "@@@_###_###",
    [includeEpisodeInShotCodes]
  );

  const renderedEpisodeCount = useMemo(() => {
    const episodeNumbers = Object.keys(episodes).map(Number);
    return episodeNumbers.length > 0 ? Math.max(...episodeNumbers) : 0;
  }, [episodes]);

  const selectedEpisodeState = selectedEpisode !== null ? episodes[selectedEpisode] : undefined;
  const selectedEpisodeSequenceCount = useMemo(() => {
    if (!selectedEpisodeState) return 0;
    return selectedEpisodeState.detectedSequenceCount
      ?? selectedEpisodeState.manualSequenceCount
      ?? Object.keys(selectedEpisodeState.sequences).length
      ?? 0;
  }, [selectedEpisodeState]);
  const selectedEpisodeSequenceEntries = useMemo(
    () => selectedEpisodeState
      ? Object.entries(selectedEpisodeState.sequences)
          .map(([sequenceNumber, sequence]) => ({ sequenceNumber: Number(sequenceNumber), sequence }))
          .sort((left, right) => left.sequenceNumber - right.sequenceNumber)
      : [],
    [selectedEpisodeState]
  );

  const sequenceColumns = useMemo(() => {
    if (selectedEpisodeSequenceEntries.length === 0) return [];
    const numCols = 6;
    const itemsPerCol = Math.ceil(selectedEpisodeSequenceEntries.length / numCols);
    const result: Array<typeof selectedEpisodeSequenceEntries> = [];
    for (let index = 0; index < numCols; index += 1) {
      const start = index * itemsPerCol;
      const end = Math.min((index + 1) * itemsPerCol, selectedEpisodeSequenceEntries.length);
      if (start < end) {
        result.push(selectedEpisodeSequenceEntries.slice(start, end));
      }
    }
    return result;
  }, [selectedEpisodeSequenceEntries]);

  const totalShots = useMemo(
    () =>
      Object.values(episodes).reduce((total, episode) => {
        return total + Object.values(episode.sequences).reduce((episodeTotal, sequence) => {
          if (!sequence.selected) return episodeTotal;
          return episodeTotal + Math.max(0, sequence.manualShotCount ?? sequence.detectedShotEstimate ?? setup.shotCount ?? DEFAULT_SHOT_COUNT);
        }, 0);
      }, 0),
    [episodes, setup.shotCount]
  );

  if (!isOpen && !isEmbedded) return null;

  const updateEpisode = (episodeNumber: number, updater: (episode: ShotCodeGeneratorEpisodeState) => ShotCodeGeneratorEpisodeState) => {
    setEpisodes((previous) => ({
      ...previous,
      [episodeNumber]: updater(previous[episodeNumber]),
    }));
  };

  const handleExpectedEpisodeCountChange = (nextValue: number) => {
    const normalized = Math.max(0, nextValue);
    setExpectedEpisodeCount(normalized);
    setEpisodes((previous) => {
      const nextSetup: ProjectSetup = {
        ...setup,
        shotCodeGeneratorEpisodes: previous,
        expectedEpisodeCount: normalized,
      };
      return buildGeneratorEpisodes(nextSetup, scriptEpisodes, normalized, manualEpisodeOverrideEnabled);
    });
  };

  const handleManualOverrideToggle = () => {
    const nextManualOverride = !manualEpisodeOverrideEnabled;
    setManualEpisodeOverrideEnabled(nextManualOverride);
    setEpisodes((previous) => {
      const nextSetup: ProjectSetup = {
        ...setup,
        shotCodeGeneratorEpisodes: previous,
        expectedEpisodeCount,
      };
      return buildGeneratorEpisodes(nextSetup, scriptEpisodes, expectedEpisodeCount, nextManualOverride);
    });
  };

  const handleEpisodeToggle = (episodeNumber: number) => {
    const episode = episodes[episodeNumber];
    if (!episode) return;
    if (!episode.hasScriptData && !manualEpisodeOverrideEnabled) return;
    setSelectedEpisode((current) => current === episodeNumber ? null : episodeNumber);
  };

  const handleSelectedEpisodeSequenceCountChange = (nextValue: number) => {
    if (selectedEpisode === null) return;
    const normalized = Math.max(0, nextValue);
    updateEpisode(selectedEpisode, (episode) => ({
      ...episode,
      isActive: episode.hasScriptData || manualEpisodeOverrideEnabled,
      isManualOverrideEnabled: true,
      manualSequenceCount: normalized,
      sequences: buildDefaultSequences(
        normalized,
        episode.sequences,
        setup.shotCount || DEFAULT_SHOT_COUNT,
        episode.hasScriptData,
      ),
    }));
  };

  const handleSequenceToggle = (sequenceNumber: number) => {
    if (selectedEpisode === null || !selectedEpisodeState) return;
    updateEpisode(selectedEpisode, (episode) => ({
      ...episode,
      sequences: {
        ...episode.sequences,
        [sequenceNumber]: {
          ...episode.sequences[sequenceNumber],
          selected: !episode.sequences[sequenceNumber]?.selected,
          manualShotCount: episode.sequences[sequenceNumber]?.manualShotCount ?? setup.shotCount ?? DEFAULT_SHOT_COUNT,
        },
      },
    }));
  };

  const updateSequenceShotCount = (sequenceNumber: number, shotCount: number) => {
    if (selectedEpisode === null) return;
    updateEpisode(selectedEpisode, (episode) => ({
      ...episode,
      sequences: {
        ...episode.sequences,
        [sequenceNumber]: {
          ...episode.sequences[sequenceNumber],
          manualShotCount: Math.max(0, shotCount),
          selected: episode.sequences[sequenceNumber]?.selected ?? true,
        },
      },
    }));
  };

  const handleGenerateClick = () => {
    if (totalShots === 0) return;

    const hasChanged = shots.some((shot) => {
      const parts = shot.shotCode.split("_");
      const expectedSegmentCount = showCode ? (includeEpisodeInShotCodes ? 4 : 3) : (includeEpisodeInShotCodes ? 3 : 2);
      if (parts.length !== expectedSegmentCount) return true;
      if (showCode && parts[0] !== showCode) return true;
      return false;
    });

    if (hasChanged) {
      setShowUpdatePrompt(true);
    } else {
      executeGenerate(false);
    }
  };

  const executeGenerate = (updateExisting: boolean) => {
    const shotCodeBreadcrumb = includeEpisodeInShotCodes
      ? `${"@".repeat(Math.max(1, showCode.length || 3))}_${"#".repeat(Math.max(1, epPattern.length || 3))}_${"#".repeat(Math.max(1, seqPattern.length || 3))}_${"#".repeat(Math.max(1, shotPattern.length || 3))}`
      : `${"@".repeat(Math.max(1, showCode.length || 3))}_${"#".repeat(Math.max(1, seqPattern.length || 3))}_${"#".repeat(Math.max(1, shotPattern.length || 3))}`;
    const legacyEpisodeSequenceData = buildLegacyEpisodeSequenceData(episodes, setup.shotCount || DEFAULT_SHOT_COUNT);

    onUpdateSetup({
      showCode,
      projectType: setup.projectType || "episodic",
      shotCodeBreadcrumb,
      shotCodePattern: shotCodeBreadcrumb,
      episodeCount: expectedEpisodeCount,
      sequenceCount: selectedEpisodeSequenceCount,
      episodeSequenceData: legacyEpisodeSequenceData,
      expectedEpisodeCount,
      detectedEpisodeCount,
      manualEpisodeOverrideEnabled,
      shotCodeGeneratorEpisodes: episodes,
    });

    const newShots: Shot[] = [];

    Object.entries(episodes).forEach(([episodeKey, episode]) => {
      const episodeNumber = Number(episodeKey);
      const sortedSequences = Object.entries(episode.sequences)
        .filter(([, sequence]) => sequence.selected)
        .map(([sequenceNumber, sequence]) => ({ sequenceNumber: Number(sequenceNumber), sequence }))
        .sort((left, right) => left.sequenceNumber - right.sequenceNumber);

      sortedSequences.forEach(({ sequenceNumber, sequence }) => {
        const shotCount = Math.max(0, sequence.manualShotCount ?? sequence.detectedShotEstimate ?? setup.shotCount ?? DEFAULT_SHOT_COUNT);
        for (let shotIndex = 1; shotIndex <= shotCount; shotIndex += 1) {
          const shotCode = generateShotCode(episodeNumber, sequenceNumber, shotIndex);
          const exists = shots.some((shot) => shot.shotCode === shotCode);
          if (!exists) {
            newShots.push({
              id: uuidv4(),
              active: true,
              shotCode,
              description: "",
              scopeOfWork: "",
              frames: 0,
              quantity: 1,
              assumptions: "",
              internalNotes: "",
              days: {
                supervisor: 0,
                concept: 0,
                dmp: 0,
                comp: 0,
                roto: 0,
                prep: 0,
                gfx: 0,
                cameraTR: 0,
                objectTR: 0,
                rANIM: 0,
                layout: 0,
                anim: 0,
                light: 0,
                fx: 0,
              },
            });
          }
        }
      });
    });

    onGenerate(newShots, updateExisting, showCode);
    setShowUpdatePrompt(false);
    onClose();
  };

  const content = (
    <div className={cn(
      "bg-white flex flex-col overflow-hidden",
      isEmbedded ? "h-full" : "rounded-2xl shadow-xl border border-slate-200 w-full max-w-7xl max-h-[90vh]"
    )}>
      {!isEmbedded && (
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-yellow-400 flex items-center justify-center shadow-sm">
              <Wand2 className="w-1.5 h-1.5 text-yellow-900" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Shot Code Generator</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="p-6 overflow-y-auto space-y-6 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">Show Code</label>
            <input
              type="text"
              value={showCode}
              onChange={(event) => setShowCode(event.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="e.g. VINE"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">Season</label>
            <input
              type="number"
              min="1"
              value={season || ""}
              onChange={(event) => setSeason(Math.max(1, parseInt(event.target.value, 10) || 1))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="e.g. 1"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">Episode Count</label>
            <input
              type="number"
              min="0"
              value={expectedEpisodeCount || ""}
              onChange={(event) => handleExpectedEpisodeCountChange(parseInt(event.target.value, 10) || 0)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="e.g. 8"
            />
            <p className="text-[11px] text-slate-500">
              Episode count can represent the full expected show. Episodes without imported scripts remain inactive until script data is added or manual override is enabled.
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">Sequence Count</label>
            <input
              type="number"
              min="0"
              value={selectedEpisodeSequenceCount || ""}
              onChange={(event) => handleSelectedEpisodeSequenceCountChange(parseInt(event.target.value, 10) || 0)}
              disabled={!selectedEpisodeState || (selectedEpisodeState.hasScriptData && !manualEpisodeOverrideEnabled)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm disabled:bg-slate-100 disabled:text-slate-400"
              placeholder="e.g. 42"
            />
            <p className="text-[11px] text-slate-500">Sequence count applies to the selected episode.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <button
            type="button"
            onClick={handleManualOverrideToggle}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
              manualEpisodeOverrideEnabled
                ? "border-indigo-600 bg-indigo-600 text-white"
                : "border-slate-300 bg-white text-slate-700 hover:border-indigo-300"
            )}
          >
            {manualEpisodeOverrideEnabled ? <Wand2 className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            {manualEpisodeOverrideEnabled ? "Manual override enabled" : "Enable manual override"}
          </button>
          <div className="text-sm text-slate-600">
            Script-derived automation drives active episodes and detected sequence counts where script data exists. Manual override is the escape hatch for creating shot codes before scripts arrive.
          </div>
          <div className="ml-auto text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            {detectedEpisodeCount} imported {detectedEpisodeCount === 1 ? "episode" : "episodes"} • {renderedEpisodeCount} shown
          </div>
        </div>

        <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200 relative">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800">Shot Code Breadcrumb: <span className="text-slate-400 font-mono ml-2">eg {breadcrumbExample}</span></h3>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="px-3 py-2 bg-slate-200 border border-slate-300 rounded-lg text-sm font-mono text-slate-600 min-w-[80px] text-center">
                {showCode || "SHOW"}
              </div>
            </div>

            {includeEpisodeInShotCodes && (
              <div className="flex items-center gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase">ep</label>
                <input
                  type="text"
                  value={epPattern}
                  onChange={(event) => setEpPattern(event.target.value.replace(/[^#]/g, ""))}
                  className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-mono text-center"
                  placeholder="####"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-500 uppercase">seq</label>
              <input
                type="text"
                value={seqPattern}
                onChange={(event) => setSeqPattern(event.target.value.replace(/[^#]/g, ""))}
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-mono text-center"
                placeholder="###"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-500 uppercase">shot</label>
              <input
                type="text"
                value={shotPattern}
                onChange={(event) => setShotPattern(event.target.value.replace(/[^#]/g, ""))}
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-mono text-center"
                placeholder="####"
              />
            </div>
          </div>
          <div className="flex items-end justify-between mt-2">
            <p className="text-[10px] text-slate-500 italic">Type hashtags (#) to determine the number of digits for each section.</p>
            <div className="text-xs font-mono text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100 shadow-sm">
              Shot Code Example: <span className="font-bold">{exampleShotCode}</span>
            </div>
          </div>
        </div>

        {renderedEpisodeCount > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Select Episode to Configure</h3>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: renderedEpisodeCount }, (_, index) => index + 1).map((episodeNumber) => {
                const episode = episodes[episodeNumber];
                const isSelectable = Boolean(episode?.hasScriptData || manualEpisodeOverrideEnabled);
                const sequenceCountLabel = episode?.detectedSequenceCount ?? episode?.manualSequenceCount ?? 0;
                return (
                  <button
                    key={episodeNumber}
                    onClick={() => handleEpisodeToggle(episodeNumber)}
                    className={cn(
                      "min-w-[104px] rounded-xl border px-3 py-2 text-left transition-all",
                      selectedEpisode === episodeNumber
                        ? "border-indigo-600 bg-indigo-600 text-white shadow-sm"
                        : isSelectable
                          ? "bg-white border-slate-200 text-slate-700 hover:border-indigo-300"
                          : "bg-slate-100 border-slate-200 text-slate-400"
                    )}
                  >
                    <div className="text-xs font-semibold">Ep {episodeNumber.toString().padStart(2, "0")}</div>
                    <div className={cn("mt-1 text-[11px]", selectedEpisode === episodeNumber ? "text-indigo-100" : "text-slate-500")}>
                      {episode?.hasScriptData
                        ? `${sequenceCountLabel} imported ${sequenceCountLabel === 1 ? "sequence" : "sequences"}`
                        : manualEpisodeOverrideEnabled
                          ? "Manual episode"
                          : "Inactive until script import"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {selectedEpisodeState && selectedEpisode !== null && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Configure Sequences for Episode {selectedEpisode.toString().padStart(2, "0")}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedEpisodeState.hasScriptData
                    ? `Script-derived data from ${selectedEpisodeState.sourceDocumentName || "imported script"} is driving this episode.`
                    : manualEpisodeOverrideEnabled
                      ? "No script yet for this episode. Manual override is letting you configure it now."
                      : "No script data is available for this episode yet."}
                </p>
              </div>
              <div className="text-sm text-slate-500">
                {selectedEpisodeState.hasScriptData ? (
                  <span>Detected sequence count: <span className="font-semibold text-slate-700">{selectedEpisodeState.detectedSequenceCount || 0}</span></span>
                ) : (
                  <span>Manual sequence count: <span className="font-semibold text-slate-700">{selectedEpisodeState.manualSequenceCount || 0}</span></span>
                )}
              </div>
            </div>

            {selectedEpisodeSequenceEntries.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {sequenceColumns.map((column, columnIndex) => (
                  <div key={columnIndex} className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-8">Sel</th>
                          <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Seq</th>
                          <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-16">Shots</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {column.map(({ sequenceNumber, sequence }) => (
                          <tr
                            key={sequenceNumber}
                            className={cn(
                              "hover:bg-slate-50 transition-colors",
                              sequence.selected && "bg-indigo-50/30"
                            )}
                          >
                            <td className="px-2 py-1.5">
                              <input
                                type="checkbox"
                                checked={sequence.selected}
                                onChange={() => handleSequenceToggle(sequenceNumber)}
                                className="w-3 h-3 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                              />
                            </td>
                            <td className="px-2 py-1.5 text-[10px] font-medium text-slate-700">
                              {sequenceNumber.toString().padStart(3, "0")}
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="number"
                                min="0"
                                value={sequence.manualShotCount ?? sequence.detectedShotEstimate ?? ""}
                                onChange={(event) => updateSequenceShotCount(sequenceNumber, parseInt(event.target.value, 10) || 0)}
                                className="w-full px-1 py-0.5 text-[10px] border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none"
                                placeholder={String(setup.shotCount || DEFAULT_SHOT_COUNT)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                {manualEpisodeOverrideEnabled
                  ? "Set a sequence count for this episode to start configuring sequences."
                  : "This episode has no imported script data yet. Enable manual override to configure it early."}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
        <div className="text-sm text-slate-500">
          {totalShots > 0 ? (
            <span>Will create <span className="font-bold text-slate-700">{totalShots}</span> total shots across all configured episodes</span>
          ) : (
            <span>Configure episodes and sequences to generate shots</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!isEmbedded && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleGenerateClick}
            disabled={totalShots === 0}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            Generate Shots
          </button>
        </div>
      </div>
    </div>
  );

  if (isEmbedded) return content;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      {content}

      {showUpdatePrompt && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6 space-y-6">
            <div className="flex items-center gap-3 text-amber-600">
              <AlertCircle className="w-6 h-6" />
              <h3 className="text-lg font-bold text-slate-900">Show Code Changed</h3>
            </div>
            <p className="text-slate-600 text-sm">
              It looks like the show code has changed to <span className="font-bold">"{showCode || "(none)"}"</span>. Do you want to update the existing shots to use this new show code, or just add the new shots?
            </p>
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                onClick={() => setShowUpdatePrompt(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => executeGenerate(false)}
                className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Add Only
              </button>
              <button
                onClick={() => executeGenerate(true)}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                Update Existing & Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
